/**
 * Цвет и яркость картинки — по её крошечной копии.
 *
 * Главная подкрашивает фон основным цветом постера в фокусе, а логотип тайтла
 * должен знать, тёмный ли он: чёрная надпись на тёмном кадре не читается.
 * Оба ответа даёт одна и та же дешёвая операция — копия 8×8 на холсте. Картинки
 * приходят с нашего же домена, поэтому холст не «отравлен» и пиксели читаются
 * без CORS.
 *
 * Ответ кэшируется по адресу: фокус гуляет по одним и тем же карточкам, и
 * второй раз считать то же самое незачем.
 */

export interface ImageTone {
  /** Цвет для мягкого градиента под тайтлом (`rgb(…)`), уже приглушённый. */
  ambient: string;
  /** Средняя яркость непрозрачных пикселей, 0…1. */
  luminance: number;
}

const SIDE = 8;
const cache = new Map<string, Promise<ImageTone | null>>();

/** Цвет и яркость картинки; `null` — посчитать не вышло (нет холста, ошибка). */
export function imageTone(url: string): Promise<ImageTone | null> {
  const known = cache.get(url);
  if (known) return known;
  const pending = measure(url).catch(() => null);
  cache.set(url, pending);
  return pending;
}

async function measure(url: string): Promise<ImageTone | null> {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = SIDE;
  canvas.height = SIDE;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(image, 0, 0, SIDE, SIDE);
  const { data } = context.getImageData(0, 0, SIDE, SIDE);

  // Цвет — среднее, взвешенное по насыщенности: серые и чёрные поля постера
  // (рамки, ночное небо) иначе утопили бы его главный цвет в буром.
  let weight = 0;
  let red = 0;
  let green = 0;
  let blue = 0;
  let lumaSum = 0;
  let alphaSum = 0;
  for (let index = 0; index < data.length; index += 4) {
    const r = data[index]! / 255;
    const g = data[index + 1]! / 255;
    const b = data[index + 2]! / 255;
    const a = data[index + 3]! / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const w = a * (0.08 + (max - min)) * (0.3 + max);
    red += r * w;
    green += g * w;
    blue += b * w;
    weight += w;
    lumaSum += (0.2126 * r + 0.7152 * g + 0.0722 * b) * a;
    alphaSum += a;
  }
  if (weight === 0 || alphaSum === 0) return null;
  return {
    ambient: softened(red / weight, green / weight, blue / weight),
    luminance: lumaSum / alphaSum,
  };
}

/**
 * Цвет для подсветки фона: насыщенность и светлота зажаты в коридор, где
 * любой постер даёт благородный отсвет, а не кислотное пятно или грязь.
 */
function softened(r: number, g: number, b: number): string {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;
  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === r) hue = (g - b) / delta + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue /= 6;
  }
  const s = Math.min(0.78, Math.max(0.32, saturation * 1.15));
  const l = Math.min(0.5, Math.max(0.36, lightness));
  const [rr, gg, bb] = hslToRgb(hue, s, l);
  return `rgb(${rr}, ${gg}, ${bb})`;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)].map((value) => Math.round(value * 255)) as [
    number,
    number,
    number,
  ];
}
